import _defineProperty from "@babel/runtime/helpers/defineProperty";
/*
 *                     NOTICE
 *            COPYRIGHT 2022 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import * as AbReactResources from "abreact/generated/locale/AbReactResources";
import { AbSDKRegisteredRoute } from "abreact/incubator/ui/app/router/simple/AbSDKRegisteredRoute";
import { RootAliasRouteSpecFn } from "abreact/incubator/ui/app/router/simple/RootAliasRoute";
import { DebugUtils } from "abweb/utils/DebugUtils";
import { LogAndRenderErrorPipeline } from "abweb/utils/error/LogAndRenderErrorPipeline";
import { LogUtils } from "abweb/utils/log/LogUtils";
const LOG = LogUtils.getLogger(__filename);
export class RouteRegistryImpl {
  constructor() {
    _defineProperty(this, "_registerRoutesByRouteTemplate", new Map());
  }
  registerRoute(routeSpec) {
    const routeTemplate = routeSpec.routeTemplate;
    const existRegisteredRoute = this.existRegisteredRoute(routeTemplate);
    if (existRegisteredRoute) {
      // Would rather return existing route instead of 'undefined!' as otherwise callers get NPE
      // when accessing members of returned RegisteredRoute (and want to 'gracefully' handle route collisions,
      // which can cause the app to fail to initialize).  So instead, return pre-existing route.
      // However, no guarantee existRegisteredRoute has same <RoutePropsT, RouteSpecT> as duplicate route,
      // so it is a dangerous cast that might blow up when they actually try to mount the route
      // (but this is better than NPE which prevents app initialization...).  Given that
      // they were warned by the above error that this is a problematic duplicate route registration,
      // that seems fair.
      //
      // Other potential idea: generate AbSDKRegisterRoute that renders 'duplicate route $routeTemplate ignored'
      // return undefined!;  // This will cause workbench NPE when encounter duplicate samplers.
      return existRegisteredRoute;
    }

    // Simplifies RouteNavigationService boilerplate
    // - saves 'new AbSDKRoute' constructor call, and return type narrowed to RegisteredRoute (goto methods) only
    const abSDKRoute = new AbSDKRegisteredRoute(routeSpec);
    this._registerRoutesByRouteTemplate.set(routeTemplate, abSDKRoute);
    return abSDKRoute;
  }
  existRegisteredRoute(routeTemplate) {
    const existRegisteredRoute = this._registerRoutesByRouteTemplate.get(routeTemplate);
    if (existRegisteredRoute) {
      const errMsg = AbReactResources.routeRegistry_duplicateRoute_detail(routeTemplate);
      if (DebugUtils.THROW_ON_DEV_ERROR) LogAndRenderErrorPipeline.pipeline.reportError(new Error(errMsg));else LOG.error(errMsg);
    }
    return existRegisteredRoute;
  }

  /** Provide router-specific configuration that is used by the router initialization infrastructure */
  get registerRoutesByRouteTemplate() {
    return this._registerRoutesByRouteTemplate;
  }

  /**
   * Register an absdk route as the default route, and ensure no other absdk routes are registered as a default route.
   * Only reasons about registered AbSDK routes.  It will raise an error if another route is already registered as a default route,
   * or if the defaultRoute can not be found in the registered routes.
   */
  setDefaultRoute(defaultRegRoute) {
    const rootAliasRegRoute = new AbSDKRegisteredRoute(RootAliasRouteSpecFn(defaultRegRoute));
    const rootAliasRouteTemplate = rootAliasRegRoute.routeSpec.routeTemplate;
    if (this._registerRoutesByRouteTemplate.has(rootAliasRouteTemplate)) {
      throw new Error(`Can not register root alias route '${rootAliasRouteTemplate}' route as it has already been registered.`);
    }
    this._registerRoutesByRouteTemplate.set(rootAliasRouteTemplate, rootAliasRegRoute);
  }
}
//# sourceMappingURL=RouteRegistryImpl.js.map
